import React, { useEffect, useId, useRef, useState } from 'react';

// Barre indépendante des préférences de masquage des scrollbars du système.
export default function PersistentScrollbar({ scrollRef, lang }) {
  const id = useId();
  const trackRef = useRef(null);
  const drag = useRef(null);
  const [metrics, setMetrics] = useState({ max: 0, top: 0, height: 0, thumb: 0 });
  useEffect(() => {
    const area = scrollRef.current;
    const track = trackRef.current;
    if (!area || !track) return undefined;
    const previousId = area.id;
    if (!previousId) area.id = id;
    const measure = () => {
      const height = track.clientHeight;
      const max = Math.max(0, area.scrollHeight - area.clientHeight);
      const thumb = Math.min(height, Math.max(28, height * area.clientHeight / Math.max(1, area.scrollHeight)));
      const top = Math.max(0, Math.min(max, area.scrollTop));
      setMetrics({ max, top, height, thumb });
    };
    const resize = new ResizeObserver(measure);
    const observe = () => {
      resize.disconnect(); resize.observe(area); resize.observe(track);
      Array.from(area.children).forEach(child => resize.observe(child));
      measure();
    };
    const mutation = new MutationObserver(observe);
    mutation.observe(area, { childList: true, subtree: true, characterData: true });
    area.addEventListener('scroll', measure, { passive: true });
    observe();
    return () => {
      resize.disconnect(); mutation.disconnect(); area.removeEventListener('scroll', measure);
      if (!previousId) area.removeAttribute('id');
    };
  }, [scrollRef, id]);
  const travel = Math.max(0, metrics.height - metrics.thumb);
  const thumbTop = metrics.max ? travel * metrics.top / metrics.max : 0;
  function pointerDown(event) {
    if (!metrics.max) return;
    event.preventDefault(); event.currentTarget.focus();
    const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
    const onThumb = y >= thumbTop && y <= thumbTop + metrics.thumb;
    const offset = onThumb ? y - thumbTop : metrics.thumb / 2;
    drag.current = { offset };
    event.currentTarget.setPointerCapture(event.pointerId);
    if (!onThumb && travel) scrollRef.current.scrollTop = (y - offset) / travel * metrics.max;
  }
  function pointerMove(event) {
    if (!drag.current || !travel) return;
    const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
    scrollRef.current.scrollTop = (y - drag.current.offset) / travel * metrics.max;
  }
  function keyDown(event) {
    const area = scrollRef.current;
    if (!area) return;
    const positions = { ArrowDown: area.scrollTop + 40, ArrowUp: area.scrollTop - 40,
      PageDown: area.scrollTop + area.clientHeight, PageUp: area.scrollTop - area.clientHeight,
      Home: 0, End: metrics.max };
    if (positions[event.key] === undefined) return;
    event.preventDefault(); area.scrollTop = positions[event.key];
  }
  return <div ref={trackRef} className="student-scrollbar" role="scrollbar" tabIndex={0}
    aria-label={lang === 'en' ? 'Scroll through the form' : 'Faire défiler le formulaire'}
    aria-controls={scrollRef.current?.id || id} aria-orientation="vertical"
    aria-valuemin={0} aria-valuemax={Math.round(metrics.max)} aria-valuenow={Math.round(metrics.top)}
    aria-disabled={!metrics.max} onWheel={event => {
      const area = scrollRef.current;
      if (area) area.scrollTop += event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? area.clientHeight : 1);
    }} onKeyDown={keyDown} onPointerDown={pointerDown} onPointerMove={pointerMove}
    onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
    onLostPointerCapture={() => { drag.current = null; }}>
    <span className="student-scrollbar-thumb" style={{ height: metrics.thumb, transform: `translateY(${thumbTop}px)` }} />
  </div>;
}
